'use client';

import * as React from 'react';
import Link from 'next/link';
import { Bot, Menu, Store, Plus, Zap, Plug, ChevronRight, Loader2, FileText } from 'lucide-react';

import { NavAgents } from '@/components/sidebar/nav-agents';
import { NavUserWithTeams } from '@/components/sidebar/nav-user-with-teams';
import { OtterzLogo } from '@/components/sidebar/otterz-logo';
import { BusinessFileManager } from '@/components/sidebar/business-file-manager';
import { BusinessSelector } from '@/components/sidebar/business-selector';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useFeatureFlags } from '@/lib/feature-flags';
import { useCreateNewAgent } from '@/hooks/react-query/agents/use-agents';
import { useAccounts } from '@/hooks/use-accounts';
import { Button } from '../ui/button';
import { useIsAccountant } from '@/hooks/use-accountant';

export function SidebarLeft({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { state, setOpen, setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const [user, setUser] = useState<{
    name: string;
    email: string;
    avatar: string;
  }>({
    name: 'Loading...',
    email: 'loading@example.com',
    avatar: '',
  });

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { flags, loading: flagsLoading } = useFeatureFlags(['custom_agents', 'agent_marketplace', 'app_integrations']);
  const { data: accounts } = useAccounts();
  const { data: isAccountant, isLoading: isAccountantLoading } = useIsAccountant();
  const customAgentsEnabled = flags.custom_agents;
  const marketplaceEnabled = flags.agent_marketplace;
  const appIntegrationsEnabled = flags.app_integrations;
  const createNewAgentMutation = useCreateNewAgent();
  const [showNewAgentDialog, setShowNewAgentDialog] = useState(false);

  // Get personal account
  const personalAccount = React.useMemo(
    () => accounts?.find((account) => account.personal_account),
    [accounts],
  );
  
  // Accountants have restricted access - no chat or agents
  const showChatAndAgents = !isAccountant;

  
  useEffect(() => {
    const fetchUserData = async () => {
      const supabase = createClient();
      const { data } = await supabase.auth.getUser();

      if (data.user) {
        setUser({
          name:
            data.user.user_metadata?.name ||
            data.user.email?.split('@')[0] ||
            'User',
          email: data.user.email || '',
          avatar: data.user.user_metadata?.avatar_url || '',
        });
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        setOpen(!state.startsWith('expanded'));
        window.dispatchEvent(
          new CustomEvent('sidebar-left-toggled', {
            detail: { expanded: !state.startsWith('expanded') },
          }),
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, setOpen]);


  const handleCreateNewAgent = () => {
    createNewAgentMutation.mutate();
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r-0 bg-background/95 backdrop-blur-sm [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
      {...props}
    >
      <SidebarHeader className="px-2 py-2">
        <div className="flex h-[40px] items-center px-1 relative">
          <Link href="/dashboard">
            <OtterzLogo />
          </Link>
          {state !== 'collapsed' && (
            <div className="ml-2 transition-all duration-200 ease-in-out whitespace-nowrap">
              <span 
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: '#ff6700' }}
              >
                OTTERZ-NYRA
              </span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {state !== 'collapsed' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <SidebarTrigger className="h-8 w-8" />
                </TooltipTrigger>
                <TooltipContent>Toggle sidebar (CMD+B)</TooltipContent>
              </Tooltip>
            )}
            {isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setOpenMobile(true)}
                    className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent"
                  >
                    <Menu className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Open menu</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent className="[&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
        <SidebarGroup>
          {/* New Chat - only for non-accountant users */}
          {showChatAndAgents && (
            <Link href="/dashboard">
              <SidebarMenuButton className={cn('hover:bg-orange-50/50', {
                'bg-accent text-accent-foreground font-medium': pathname === '/dashboard',
              })}>
                <Plus className="h-4 w-4 mr-1" style={{ color: pathname === '/dashboard' ? 'inherit' : '#ff6700' }} />
                <span className="flex items-center justify-between w-full">
                  New Chat
                  {pathname === '/dashboard' && (
                    <span 
                      className="text-xs font-medium"
                      style={{ color: '#ff6700' }}
                    >
                      •
                    </span>
                  )}
                </span>
              </SidebarMenuButton>
            </Link>
          )}
          
          {/* Accountant Welcome Message */}
          {isAccountant && (
            <div className="px-3 py-2 mb-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" style={{ color: '#ff6700' }} />
                <span>Accountant Access</span>
              </div>
            </div>
          )}
          
          {!flagsLoading && showChatAndAgents && false && (
            <SidebarMenu>
              <Collapsible
                defaultOpen={pathname?.includes('/agents')}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Agents"
                      className="hover:bg-orange-50/30"
                    >
                      <Bot className="h-4 w-4 mr-1" style={{ color: pathname?.includes('/agents') ? '#ff6700' : 'inherit' }} />
                      <span className="flex items-center">
                        Agents
                        {pathname?.includes('/agents') && (
                          <span 
                            className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full bg-orange-50"
                            style={{ color: '#ff6700' }}
                          >
                            ●
                          </span>
                        )}
                      </span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton className={cn('pl-3 hover:bg-orange-50/30', {
                          'bg-accent text-accent-foreground font-medium': pathname === '/agents' && searchParams.get('tab') === 'marketplace',
                        })} asChild>
                          <Link href="/agents?tab=marketplace">
                            <span className="flex items-center">
                              Explore
                              {pathname === '/agents' && searchParams.get('tab') === 'marketplace' && (
                                <span 
                                  className="ml-auto text-xs"
                                  style={{ color: '#ff6700' }}
                                >
                                  ●
                                </span>
                              )}
                            </span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton className={cn('pl-3 hover:bg-orange-50/30', {
                          'bg-accent text-accent-foreground font-medium': pathname === '/agents' && (searchParams.get('tab') === 'my-agents' || searchParams.get('tab') === null),
                        })} asChild>
                          <Link href="/agents?tab=my-agents">
                            <span className="flex items-center">
                              My Agents
                              {pathname === '/agents' && (searchParams.get('tab') === 'my-agents' || searchParams.get('tab') === null) && (
                                <span 
                                  className="ml-auto text-xs"
                                  style={{ color: '#ff6700' }}
                                >
                                  ●
                                </span>
                              )}
                            </span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton 
                          onClick={() => setShowNewAgentDialog(true)}
                          className="cursor-pointer pl-3 hover:bg-orange-50/30"
                        >
                          <span className="flex items-center">
                            <Plus className="h-3 w-3 mr-2" style={{ color: '#ff6700' }} />
                            New Agent
                          </span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          )}
          {/* Business Selector - allows switching between businesses */}
          <BusinessSelector className="mt-2 mb-1" />
          
          {/* Integrations - available for all users including accountants */}
          {!flagsLoading && appIntegrationsEnabled && (
            <Link href="/settings/credentials">
              <SidebarMenuButton className={cn('hover:bg-orange-50/30', {
                'bg-accent text-accent-foreground font-medium': pathname === '/settings/credentials',
              })}>
                <Plug className="h-4 w-4 mr-1" style={{ color: pathname === '/settings/credentials' ? '#ff6700' : 'inherit' }} />
                <span className="flex items-center justify-between w-full">
                  Integrations
                  {pathname === '/settings/credentials' && (
                    <span 
                      className="text-xs font-medium"
                      style={{ color: '#ff6700' }}
                    >
                      ●
                    </span>
                  )}
                </span>
              </SidebarMenuButton>
            </Link>
          )}
          
          {/* Files - available for all users including accountants */}
          {/* For accountants, we don't need accountId since they use activeBusiness from context */}
          {(personalAccount || isAccountant) && (
            <BusinessFileManager 
              accountId={personalAccount?.account_id}
            />
          )}
        </SidebarGroup>
        
        {/* Chat history / Nav Agents - only for non-accountant users */}
        {showChatAndAgents && <NavAgents />}
      </SidebarContent>

      <SidebarFooter>
        {state === 'collapsed' && (
          <div className="mt-2 flex justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarTrigger className="h-8 w-8" />
              </TooltipTrigger>
              <TooltipContent>Expand sidebar (CMD+B)</TooltipContent>
            </Tooltip>
          </div>
        )}
        <NavUserWithTeams user={user} />
      </SidebarFooter>
      <SidebarRail />
      <AlertDialog open={showNewAgentDialog} onOpenChange={setShowNewAgentDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <Bot className="h-5 w-5 mr-2" style={{ color: '#ff6700' }} />
              Create New Agent
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new agent with a default name and description.
              <span 
                className="block mt-2 text-xs font-medium"
                style={{ color: '#ff6700' }}
              >
                Ready to build something amazing?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCreateNewAgent}
              className="hover:bg-orange-600"
              style={{ backgroundColor: '#ff6700' }}
            >
              {createNewAgentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sidebar>
  );
}
